import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * OmnibarFilterStrip
 *
 * A floating "mini stream" of Collections matching the live omnibar
 * query. Mounted inside `OmnibarHost` and anchored a comfortable
 * distance above the `<DraftBar />` pill — only ever rendered while
 * the bar is in Ask mode and `app.state.ui.omnibarQuery` is non-empty
 * (the host gates mounting on those two conditions, so this component
 * can assume both are true).
 *
 * Layout shape mirrors the home stream's `groupBy="none"` flat grid so
 * the cards read as the same UI primitive you see on the rest of the
 * surface — just at smaller scale:
 *   - 5-column CSS grid (3-column on mobile).
 *   - Each cell renders a real `<CollectionBlockView />`. The card's
 *     dimensions (`--card-width`, peek geometry, height, footer
 *     padding) are scaled down via specificity-bound overrides in
 *     `omnibar-filter-strip.scss` so the rich peek-stack visualisation
 *     still works inside the smaller footprint.
 *   - Two rows max, period. The JS-side `MAX_RENDERED_RESULTS` cap
 *     (`2 * COLUMNS`) keeps the DOM from holding more cards than a
 *     desktop viewer can see, and `overflow: hidden` on the strip
 *     clips anything else (e.g. mobile's 3-col grid receiving 10
 *     items) so the visible footprint is always exactly two rows.
 *
 * Click handling: `<CollectionBlockView />`'s default click navigates
 * to `/collection/:id`. We don't override `onOpen` because the strip's
 * job is just "land on the right collection from the bar"; the omnibar
 * itself stays mounted across the route change so the user's draft
 * text survives if they want to keep typing.
 */
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collectionStore } from "@/data/collection-store";
import { CollectionBlockView } from "@/components/collection-block-view/collection-block-view";
import { useCollectionUrlBuilder } from "@/lib/urls";
import { minimizeOmnibar, registerOmnibarFirstResultOpener } from "./omnibar-bridge";
import "./omnibar-filter-strip.scss";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
// Desktop column count — must stay in sync with the
// `grid-template-columns: repeat(5, ...)` in `omnibar-filter-strip.scss`.
// Drives the partial-row placeholder padding so a row that doesn't
// have 5 real Collections still reads as a complete row of 5 slots.
const COLUMNS = 5;
// Two rows max — the strip never grows past `2 * COLUMNS` cards even
// on a wildly generic match (e.g. an empty-ish prefix that resolves
// to most of the user's collections). The strip's `max-height` +
// `overflow: hidden` in SCSS enforces the visual cap; this constant
// keeps the DOM aligned so we don't mount cards the user will never
// see. Mobile (3-col grid) still receives 10 candidates and clips
// the trailing row visually — fine because the JS doesn't know the
// active column count and the CSS clip is the source of truth.
const MAX_RENDERED_RESULTS = 2 * COLUMNS;
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function compareByActivity(a, b) {
    const aKey = a.lastAddedAt ?? a.createdAt;
    const bKey = b.lastAddedAt ?? b.createdAt;
    if (aKey === bKey)
        return b.createdAt.localeCompare(a.createdAt);
    return bKey.localeCompare(aKey);
}
function filterByQuery(collections, trimmedQuery) {
    const needle = trimmedQuery.toLowerCase();
    return collections
        .filter((c) => c.type !== "primary")
        .filter((c) => (c.title ?? "").toLowerCase().includes(needle))
        .slice()
        .sort(compareByActivity)
        .slice(0, MAX_RENDERED_RESULTS);
}
export const OmnibarFilterStrip = memo(({ query }) => {
    const navigate = useNavigate();
    const buildCollectionUrl = useCollectionUrlBuilder();
    // `useListAllWritable` returns every layer-1 Collection in the store
    // — Primary-rooted, standalone, AND Group-rooted — across every role
    // the user holds (viewer / editor / owner). Anything in the store
    // means the user has at least viewer access to it (the API only
    // surfaces collections the user can read), which is the intended
    // search universe: "show me anything I can navigate into and look
    // at." `useList()` would have hidden Group-rooted Collections.
    const { items: writableItems } = collectionStore.useListAllWritable();
    const collections = useMemo(() => writableItems.map((item) => item.collection), [writableItems]);
    const matches = useMemo(() => filterByQuery(collections, query), [collections, query]);
    // Activation path for both mouse click and Enter on a Tab-focused
    // card. We route through `onOpen` (rather than letting the card's
    // default click navigate) for two reasons:
    //   1. The card calls `e.stopPropagation()` on click, so a wrapper
    //      `onClick` never sees the bubble — `onOpen` is the only
    //      reliable hook for "result was activated from the strip".
    //   2. We need to tear the bar down on activation: clear the query,
    //      blur the focused card, drop the rise gradient. `minimizeOmnibar`
    //      runs DraftBar's registered teardown callback.
    // Order: minimize first (queues state updates), then navigate. React
    // batches both into the same render pass, so the route change and
    // the strip's unmount commit together.
    const handleOpen = useCallback((collection) => {
        minimizeOmnibar();
        navigate(buildCollectionUrl(collection));
    }, [navigate, buildCollectionUrl]);
    // Publish "activate my top match" to the bridge so DraftBar's Ask-
    // mode Enter handler can fire it instead of running the assistant
    // query when there's at least one instant result. Routing through
    // `handleOpen` means the activation path is identical to a mouse
    // click on the first card — same minimize + navigate sequence —
    // and stays correct if the strip's render order ever changes.
    // Re-registers whenever `matches` or `handleOpen` change so the
    // captured closure always sees the freshest top match.
    useEffect(() => {
        registerOmnibarFirstResultOpener(() => {
            const first = matches[0];
            if (first === undefined)
                return false;
            handleOpen(first);
            return true;
        });
        return () => registerOmnibarFirstResultOpener(undefined);
    }, [matches, handleOpen]);
    // Pad the visible cell count up to the next multiple of `COLUMNS`
    // so the last row always reads as a complete row of 5 slots — the
    // missing ones render as transparent dotted-border placeholders
    // instead of leaving a ragged edge. The math is `(C - N % C) % C`
    // (the trailing `% C` handles the exact-multiple case where
    // `C - 0 = C` would otherwise produce a fully-padded extra row).
    const placeholderCount = matches.length === 0 ? 0 : (COLUMNS - (matches.length % COLUMNS)) % COLUMNS;
    if (matches.length === 0) {
        // Empty state reuses the strip's 5-column grid so the row reads as
        // "what a result looks like" — five dotted placeholders, with the
        // status label overlaid on top. Keeps the layout's vertical
        // footprint identical between "loading / no results / has results"
        // so the bar above doesn't jump as the user types.
        return (_jsxs("div", { className: "omnibar-filter-strip omnibar-filter-strip--empty", role: "status", "aria-live": "polite", children: [Array.from({ length: COLUMNS }).map((_, i) => (_jsx("div", { className: "omnibar-filter-strip__cell omnibar-filter-strip__cell--placeholder", "aria-hidden": "true" }, `empty-${i}`))), _jsx("div", { className: "omnibar-filter-strip__empty-label", children: "No instant results" })] }));
    }
    return (_jsxs("div", { className: "omnibar-filter-strip", role: "listbox", "aria-label": `Collections matching ${query}`, children: [matches.map((collection, index) => (_jsx("div", { className: "omnibar-filter-strip__cell", "data-first": index === 0 ? "" : undefined, 
                // Don't let the card's `mousedown` steal focus from the
                // textarea while the user is mid-query — the click event
                // still fires (it's only the default focus shift that's
                // suppressed), so the underlying card's `onOpen` handler
                // runs normally.
                onMouseDown: (e) => e.preventDefault(), children: _jsx(CollectionBlockView, { collection: collection, onOpen: handleOpen }) }, collection.id))), Array.from({ length: placeholderCount }).map((_, i) => (_jsx("div", { className: "omnibar-filter-strip__cell omnibar-filter-strip__cell--placeholder", "aria-hidden": "true" }, `placeholder-${i}`)))] }));
});
